import React, { useCallback, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import useResponsiveness from '@/hooks/useResponsiveness';
import { PANEL_WIDTH, PANEL_RIGHT_OFFSET, COLLAPSED_PANEL_WIDTH } from '@/components/Chat/sourcesOutputsLayout';
import { PANEL_TOP_OFFSET, PANEL_BOTTOM_GUTTER } from '@/components/Chat/SourcesOutputsPanel';
import WorkWaitingGames from './WorkWaitingGames';

const SIDEBAR_GAP = 12;
const COLLAPSED_GAMES_HEIGHT = 54;

export default function WorkSidebar({ enabled, children, availableHeight, bottomInset = 0, onOccupiedHeightChange, sourcesCollapsed = false, onGamesExpandedChange }) {
  const { isXS } = useResponsiveness();
  const scrollRef = useRef(null);
  const scrollPending = useRef(false);
  const [gamesExpanded, setGamesExpanded] = useState(false);
  const compact = sourcesCollapsed && !gamesExpanded;
  const handleGamesExpandedChange = useCallback(value => {
    setGamesExpanded(value);
    onGamesExpandedChange?.(value);
  }, [onGamesExpandedChange]);
  const desktop = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS
    && !/Android|iPhone|iPad|Mobile/i.test(globalThis.navigator?.userAgent || '')
    && !globalThis.matchMedia?.('(pointer: coarse)')?.matches);
  const height = Math.max(0, availableHeight - PANEL_TOP_OFFSET - bottomInset - SIDEBAR_GAP);
  // Reserve the collapsed game card, even when Sources has many pinned tools.
  // Sources subtracts its own outer gutters when calculating its frame.
  const sourcesHeight = Math.max(1, height - COLLAPSED_GAMES_HEIGHT - SIDEBAR_GAP + PANEL_TOP_OFFSET + PANEL_BOTTOM_GUTTER);
  const onExpand = useCallback(() => { scrollPending.current = true; }, []);
  const onContentSizeChange = useCallback((_width, contentHeight) => {
    onOccupiedHeightChange?.(Math.min(height, contentHeight));
    if (scrollPending.current) {
      scrollPending.current = false;
      scrollRef.current?.scrollToEnd({ animated: false });
    }
  }, [height, onOccupiedHeightChange]);

  // The existing Sources surface is the only entry point. Small screens and
  // ordinary Chat keep its presentation, with no game UI added to transcripts.
  if (!enabled || !desktop || height <= 0) return children;
  return <View testID="work-right-sidebar" style={[styles.column, { height, width: compact ? COLLAPSED_PANEL_WIDTH : PANEL_WIDTH }]}>
    <ScrollView ref={scrollRef} style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator onContentSizeChange={onContentSizeChange}>
      {React.cloneElement(children, { embedded: true, availableHeight: sourcesHeight, onOccupiedHeightChange: undefined })}
      <WorkWaitingGames compact={compact} onExpand={onExpand} onExpandedChange={handleGamesExpandedChange} />
    </ScrollView>
  </View>;
}

const styles = StyleSheet.create({
  column: { position: 'absolute', top: PANEL_TOP_OFFSET, right: PANEL_RIGHT_OFFSET, width: PANEL_WIDTH, zIndex: 25, overflow: 'hidden' },
  scroll: { flex: 1 },
  content: { gap: SIDEBAR_GAP, paddingBottom: 4 },
});
